import { generateShortCodeFromEui, isValidEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";

/**
 * Returns a structured ID object for a device.
 *
 * Resolves the best available identifier in priority order:
 * 1. `verdiId` — when present, returns the stored verdiId as `displayID`
 * 2. `node_id` — if a valid EUI, returns a short human-readable code as `displayID` and the raw EUI as `eui`
 * 3. `node_id` — if present but not a valid EUI, used as `displayID` with `eui` set to "N/A"
 * 4. `EnclosureID` / `enclosureID` — used as `displayID` when `node_id` is absent
 * 5. Falls back to `"Unknown"` for all fields if no identifier is available
 *
 * This could use more work, but at least they will be a consistent method for
 * getting a display ID
 */
export function getDisplayId(device: {
    node_id?: string;
    name?: string;
    EnclosureID?: string;
    enclosureID?: string;
    verdiId?: string;
}) {
    if (!device) {
        throw new Error(`Error, cannot get display ID from ${device}`);
    }

    const id = device.node_id;

    if (device.verdiId) {
        const isValid = id ? isValidEui(id) : false;
        return { displayID: device.verdiId, eui: isValid ? id : "N/A", adminId: id ?? device.verdiId };
    }

    if (id) {
        const isValid = isValidEui(id);
        if (isValid) {
            return { displayID: generateShortCodeFromEui(id), eui: id, adminId: id };
        }
        return { displayID: id, eui: "N/A", adminId: id };
    }

    const secondaryID = device?.EnclosureID || device?.enclosureID;
    if (secondaryID) {
        return { displayID: secondaryID, eui: "N/A", adminId: secondaryID };
    }
    return { displayID: "Unknown", eui: "Unknown", adminId: "Unknown" };
}
